import { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { User, Theme, LoginRequest } from '../services/types'
import authService from '../services/authService'

interface AuthContextType {
  user: User | null
  isLoading: boolean
  isAuthenticated: boolean
  theme: Theme
  login: (credentials: LoginRequest) => Promise<void>
  logout: () => Promise<void>
  toggleTheme: () => Promise<void>
  setTheme: (theme: Theme) => Promise<void>
  refetchUser: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return context
}

interface AuthProviderProps {
  children: ReactNode
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [user, setUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [theme, setThemeState] = useState<Theme>('light')

  // Initialize auth state on mount
  useEffect(() => {
    const initAuth = async () => {
      if (authService.isAuthenticated()) {
        try {
          const userData = await authService.me()
          setUser(userData)
          applyTheme(userData.theme)
        } catch (error) {
          console.error('Failed to fetch user:', error)
          localStorage.removeItem('access_token')
          localStorage.removeItem('refresh_token')
        }
      }
      setIsLoading(false)
    }

    initAuth()
  }, [])

  // Apply theme to document
  const applyTheme = (newTheme: Theme) => {
    setThemeState(newTheme)
    if (newTheme === 'dark') {
      document.documentElement.classList.add('dark')
    } else {
      document.documentElement.classList.remove('dark')
    }
  }

  // Login
  const login = async (credentials: LoginRequest) => {
    const { user: userData } = await authService.login(credentials)
    setUser(userData)
    applyTheme(userData.theme)
  }

  // Logout
  const logout = async () => {
    await authService.logout()
    setUser(null)
  }

  // Toggle theme
  const toggleTheme = async () => {
    if (!user) return

    const newTheme = theme === 'light' ? 'dark' : 'light'
    const updatedUser = await authService.updateTheme(newTheme)
    setUser(updatedUser)
    applyTheme(newTheme)
  }

  // Set specific theme
  const setTheme = async (newTheme: Theme) => {
    if (!user || theme === newTheme) return

    const updatedUser = await authService.updateTheme(newTheme)
    setUser(updatedUser)
    applyTheme(newTheme)
  }

  // Refetch user data (after avatar upload, profile update, etc)
  const refetchUser = async () => {
    if (!user) return

    try {
      const updatedUser = await authService.me()
      setUser(updatedUser)
      applyTheme(updatedUser.theme)
    } catch (error) {
      console.error('Failed to refetch user:', error)
    }
  }

  const value: AuthContextType = {
    user,
    isLoading,
    isAuthenticated: !!user,
    theme,
    login,
    logout,
    toggleTheme,
    setTheme,
    refetchUser,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export default AuthContext
